import React, { createContext, useContext, useState, useEffect } from "react";

// Típus definiálása a BeforeInstallPromptEvent-hez
interface BeforeInstallPromptEvent extends Event {
    prompt: () => Promise<void>;
    userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

interface InstallContextType {
    isInstallable: boolean;
    installApp: () => Promise<void>;
    showInstallBanner: boolean;
    setShowInstallBanner: (show: boolean) => void;
}

const InstallContext = createContext<InstallContextType | undefined>(undefined);

export const InstallProvider: React.FC<{ children: React.ReactNode }> = ({
    children,
}) => {
    const [deferredPrompt, setDeferredPrompt] =
        useState<BeforeInstallPromptEvent | null>(null);
    const [isInstallable, setIsInstallable] = useState(false);
    const [showInstallBanner, setShowInstallBanner] = useState(false);

    useEffect(() => {
        const handleBeforeInstallPrompt = (e: Event) => {
            // Megakadályozzuk a böngésző automatikus prompt megjelenítését
            e.preventDefault();
            // Elmentjük az eseményt
            setDeferredPrompt(e as BeforeInstallPromptEvent);
            setIsInstallable(true);
            setShowInstallBanner(true); // Opcionálisan megjeleníthetünk bannert
        };

        window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);

        const handleAppInstalled = () => {
            setDeferredPrompt(null);
            setIsInstallable(false);
            setShowInstallBanner(false);
            console.log("Az alkalmazás telepítve lett");
        };

        window.addEventListener("appinstalled", handleAppInstalled);

        // Ha már telepítve van (standalone mód)
        if (window.matchMedia("(display-mode: standalone)").matches) {
            setIsInstallable(false);
        }

        return () => {
            window.removeEventListener(
                "beforeinstallprompt",
                handleBeforeInstallPrompt
            );
            window.removeEventListener("appinstalled", handleAppInstalled);
        };
    }, []);

    const installApp = async () => {
        if (!deferredPrompt) return;

        // Megjelenítjük a telepítési promptot
        deferredPrompt.prompt();

        // Várjuk a felhasználó döntését
        const { outcome } = await deferredPrompt.userChoice;

        console.log(`A felhasználó ${outcome} a telepítést`);

        // Egyszerhasználatos, ezért töröljük
        setDeferredPrompt(null);
        setIsInstallable(false);
        setShowInstallBanner(false);
    };

    return (
        <InstallContext.Provider
            value={{
                isInstallable,
                installApp,
                showInstallBanner,
                setShowInstallBanner,
            }}
        >
            {children}
        </InstallContext.Provider>
    );
};

export const useInstall = () => {
    const context = useContext(InstallContext);
    if (context === undefined) {
        throw new Error("useInstall must be used within an InstallProvider");
    }
    return context;
};
